import i18n from "../i18n";
import { roundToDecimalPlace } from "./ArithmeticUtils/ArithmeticUtils";

/**
 * Portfolio display/input units for Duration & Work (Gantt Options).
 *
 * Core invariant: every stored/POSTed value stays in core units
 * (Duration = days, Work = hours). This module owns the *display*
 * conversion — renderers, editor presets, filter comparisons and
 * parse-backs all go through here so the factors ("days per week",
 * "days per month", "hours per day") are applied consistently.
 *
 * Never convert with Bryntum's DateHelper: it uses calendar constants
 * (week = 7 days, day = 24 h), not working-time factors.
 *
 * The cache is written by portfolioActions (getDetails/updateDetails
 * success) — before any component consumes the details — and read
 * lazily by renderers, so pre-fetch rendering falls back to core units
 * (byte-identical to pre-feature output).
 */

const DURATION_UNITS = { DAY: "day", WEEK: "week", MONTH: "month" };
const WORK_UNITS = { HOUR: "hour", DAY: "day" };

const DEFAULTS = {
	durationUnit: DURATION_UNITS.DAY,
	workUnit: WORK_UNITS.HOUR,
	daysPerWeek: 5,
	daysPerMonth: 20,
	hoursPerDay: 8,
};

const cache = { ...DEFAULTS, labels: null };

// Unit labels resolve through i18n once per settings/language change —
// never per cell: the formatters run for every visible cell on every
// scroll/refresh pass of gantts that can hold thousands of rows.
const SHORT_SUFFIX_KEYS = { day: "d", week: "w", month: "mo", hour: "h", minute: "min" };

const resolveLabels = () => {
	const one = (unit) => i18n.t(`ganttUnits.${unit}`, unit);
	const many = (unit) => i18n.t(`ganttUnits.${unit}s`, `${unit}s`);
	const short = (unit) => {
		const s = SHORT_SUFFIX_KEYS[unit] || unit;
		return i18n.t(`ganttUnits.short.${s}`, s);
	};
	cache.labels = {};
	["day", "week", "month", "hour", "minute"].forEach((unit) => {
		cache.labels[unit] = { one: one(unit), many: many(unit), short: short(unit) };
	});
};

if (i18n?.on) {
	i18n.on("languageChanged", resolveLabels);
}

const labelsFor = (unit) => {
	if (!cache.labels) resolveLabels();
	return cache.labels[unit] || { one: unit, many: `${unit}s`, short: unit };
};

const toFiniteOrDefault = (value, fallback) => {
	const n = Number(value);
	return Number.isFinite(n) && n > 0 ? n : fallback;
};

/**
 * Feed the cache from the portfolio details payload
 * (`state.portfolio.details`). Missing/invalid values fall back to the
 * core-unit defaults.
 */
export const setDisplayUnitSettings = (details) => {
	const d = details || {};
	cache.durationUnit =
		DURATION_UNITS[d.durationDisplayUnit] || DEFAULTS.durationUnit;
	cache.workUnit = WORK_UNITS[d.workDisplayUnit] || DEFAULTS.workUnit;
	cache.daysPerWeek = toFiniteOrDefault(d.daysPerWeek, DEFAULTS.daysPerWeek);
	cache.daysPerMonth = toFiniteOrDefault(d.daysPerMonth, DEFAULTS.daysPerMonth);
	// The spec's "align with daily FTE" option: when the flag is on the
	// work conversion uses the portfolio's dailyFTE instead of the
	// dedicated hours-per-day constant.
	const hoursPerDay = d.hoursPerDayUseDailyFte ? d.dailyFTE : d.hoursPerDay;
	cache.hoursPerDay = toFiniteOrDefault(hoursPerDay, DEFAULTS.hoursPerDay);
	resolveLabels();
};

export const getDisplayUnitSettings = () => ({
	durationUnit: cache.durationUnit,
	workUnit: cache.workUnit,
	daysPerWeek: cache.daysPerWeek,
	daysPerMonth: cache.daysPerMonth,
	hoursPerDay: cache.hoursPerDay,
});

export const isDurationCore = (settings = cache) =>
	settings.durationUnit === DURATION_UNITS.DAY;
export const isWorkCore = (settings = cache) =>
	settings.workUnit === WORK_UNITS.HOUR;

/** Days per active duration display unit. */
const durationFactor = (settings = cache) => {
	if (settings.durationUnit === DURATION_UNITS.WEEK) return settings.daysPerWeek;
	if (settings.durationUnit === DURATION_UNITS.MONTH) return settings.daysPerMonth;
	return 1;
};

/** Hours per active work display unit. */
const workFactor = (settings = cache) =>
	settings.workUnit === WORK_UNITS.DAY ? settings.hoursPerDay : 1;

const factorForDurationUnit = (unit, settings = cache) => {
	if (unit === DURATION_UNITS.WEEK) return settings.daysPerWeek;
	if (unit === DURATION_UNITS.MONTH) return settings.daysPerMonth;
	return 1;
};

export const toDisplayDuration = (days, settings = cache) =>
	days == null ? days : days / durationFactor(settings);

/**
 * @param magnitude value typed by the user
 * @param unit the unit Bryntum parsed ("day" | "week" | "month");
 *             omitted → the active display unit
 */
export const fromDisplayDuration = (magnitude, unit, settings = cache) =>
	magnitude == null
		? magnitude
		: magnitude * factorForDurationUnit(unit || settings.durationUnit, settings);

export const toDisplayWork = (hours, settings = cache) =>
	hours == null ? hours : hours / workFactor(settings);

/** @param unit "hour" | "day"; omitted → the active work display unit */
export const fromDisplayWork = (magnitude, unit, settings = cache) => {
	if (magnitude == null) return magnitude;
	const u = unit || settings.workUnit;
	return u === WORK_UNITS.DAY ? magnitude * settings.hoursPerDay : magnitude;
};

/**
 * Core unit keeps today's whole-number rendering (byte-identical);
 * non-core rounds to 2 decimals with trailing zeros trimmed ("2.5",
 * "3" — never "3.00"), per the spec's MSP-like decimals rule.
 */
const formatMagnitude = (value, isCore) => {
	if (value == null || !Number.isFinite(Number(value))) return null;
	const n = Number(value);
	return String(roundToDecimalPlace(n, isCore ? 0 : 2));
};

const withUnitLabel = (text, unit) => {
	const labels = labelsFor(unit);
	const label = text === "1" || text === "-1" ? labels.one : labels.many;
	return `${text} ${label}`;
};

/** 12 → "12 days" (DAY) / "2.4 weeks" (WEEK, daysPerWeek 5). */
export const formatDuration = (days, settings = cache) => {
	const text = formatMagnitude(toDisplayDuration(days, settings), isDurationCore(settings));
	return text == null ? "" : withUnitLabel(text, settings.durationUnit);
};

/** 40 → "40 hours" (HOUR) / "5 days" (DAY, hoursPerDay 8). */
export const formatWork = (hours, settings = cache) => {
	const text = formatMagnitude(toDisplayWork(hours, settings), isWorkCore(settings));
	return text == null ? "" : withUnitLabel(text, settings.workUnit);
};

const CONVERTIBLE_LAG_UNITS = new Set([
	DURATION_UNITS.DAY,
	DURATION_UNITS.WEEK,
	DURATION_UNITS.MONTH,
]);

/**
 * Compact lag for the Predecessors/Successors cell notation
 * ("12SS+2.4w"). Mirrors Bryntum's DependencyBaseModel.getLag(): empty
 * string when there is no lag, explicit sign otherwise, and raw
 * magnitudes (never rounded) for units the working-time factors don't
 * cover — an hour lag stays "+4h" exactly as getLag() printed it.
 */
export const formatLag = (lag, lagUnit, settings = cache) => {
	if (!lag) return "";
	const unit = lagUnit || DURATION_UNITS.DAY;
	const sign = lag >= 0 ? "+" : "";
	// Core portfolio, or a unit the working-time factors don't cover:
	// raw magnitude + the lag's OWN unit suffix — byte-identical to the
	// replaced dependency.getLag() ("+2w" stays "+2w" on a DAY portfolio,
	// "+4h" stays "+4h" everywhere).
	if (isDurationCore(settings) || !CONVERTIBLE_LAG_UNITS.has(unit)) {
		return `${sign}${lag}${labelsFor(unit).short}`;
	}
	const lagDays = lag * factorForDurationUnit(unit, settings);
	const text = formatMagnitude(toDisplayDuration(lagDays, settings), false);
	return `${Number(text) >= 0 ? "+" : ""}${text}${labelsFor(settings.durationUnit).short}`;
};

/**
 * Editor `allowedUnits`: active display unit first so a bare number
 * ("2.5") parses in the active unit; day always allowed as an escape
 * hatch for precise input.
 */
export const durationEditorUnits = (settings = cache) =>
	settings.durationUnit === DURATION_UNITS.DAY
		? ["day"]
		: [settings.durationUnit, "day"];

export const workEditorUnits = (settings = cache) =>
	settings.workUnit === WORK_UNITS.HOUR ? ["hour"] : ["day", "hour"];

/**
 * Editor bounds must be expressed in the active unit: Bryntum compares
 * min/max in *calendar* milliseconds (week = 7×24 h), so "1200 days"
 * would wrongly block "240 weeks" (= 1200 working days). The reverse
 * hole (day-united input passing a week bound's calendar-ms value) is
 * closed by day-based clamps in the parse-back paths.
 */
export const durationEditorBound = (days, settings = cache) => {
	const magnitude = roundToDecimalPlace(toDisplayDuration(days, settings), 2);
	return `${magnitude} ${settings.durationUnit}s`;
};

export const workEditorBound = (hours, settings = cache) => {
	const magnitude = roundToDecimalPlace(toDisplayWork(hours, settings), 2);
	return `${magnitude} ${settings.workUnit}s`;
};

const compare = (left, right, operator) => {
	switch (operator) {
		case "=":
			return left === right;
		case "!=":
			return left !== right;
		case "<":
			return left < right;
		case "<=":
			return left <= right;
		case ">":
			return left > right;
		case ">=":
			return left >= right;
		default:
			return left === right;
	}
};

const typedToCore = (typed, dimension, settings) => {
	// Bryntum duration/effort filter fields produce {magnitude, unit};
	// honor an explicitly typed unit ("10 days" while WEEK is active).
	// Plain numbers mean the active display unit.
	if (typed && typeof typed === "object" && typed.magnitude != null) {
		const magnitude = Number(typed.magnitude);
		if (!Number.isFinite(magnitude)) return null;
		return dimension === "work"
			? fromDisplayWork(magnitude, typed.unit, settings)
			: fromDisplayDuration(magnitude, typed.unit, settings);
	}
	const n = Number(typed);
	if (!Number.isFinite(n)) return null;
	return dimension === "work"
		? fromDisplayWork(n, undefined, settings)
		: fromDisplayDuration(n, undefined, settings);
};

/**
 * Filter comparison in display space, rounded to what the user actually
 * sees (2 decimals): a cell showing "2.4 weeks" must match a filter of
 * 2.4 even though the raw quotient is 2.4000000003. Nullish record
 * values never match (mirrors Bryntum's default nullish handling) —
 * except "!=", where an absent value is indeed "not equal".
 * `dimension` is "duration" | "work".
 */
export const compareInDisplayUnit = (coreValue, typedValue, operator, dimension, settings = cache) => {
	const typedCore = typedToCore(typedValue, dimension, settings);
	if (typedCore == null) return true;
	if (coreValue == null) return operator === "!=";
	const toDisplay = dimension === "work" ? toDisplayWork : toDisplayDuration;
	const recordDisplay = roundToDecimalPlace(toDisplay(coreValue, settings), 2);
	const typedDisplay = roundToDecimalPlace(toDisplay(typedCore, settings), 2);
	return compare(recordDisplay, typedDisplay, operator);
};

/**
 * Preset a Bryntum Duration/Effort editor field to the display unit so
 * what the user sees, edits and types (bare numbers) is the active
 * unit. 4-decimal magnitudes keep the round-trip loss below scheduling
 * resolution when the value is committed untouched.
 */
export const presetUnitField = (field, coreValue, dimension, settings = cache) => {
	if (!field || coreValue == null) return;
	const isWork = dimension === "work";
	field.decimalPrecision = 2;
	field.value = {
		magnitude: roundToDecimalPlace(
			isWork ? toDisplayWork(coreValue, settings) : toDisplayDuration(coreValue, settings),
			4
		),
		unit: isWork ? settings.workUnit : settings.durationUnit,
	};
};

/**
 * Restore core units on a record that an editor preset or dialog field
 * left week/month (or work-day) united. Same span, day/hour united —
 * every legacy consumer and DTO builder assumes core units at rest.
 */
export const normalizeRecordToCoreUnits = (record) => {
	if (!record) return;
	if (record.durationUnit && record.durationUnit !== "day" && record.duration != null) {
		record.set("fullDuration", {
			magnitude: fromDisplayDuration(record.duration, record.durationUnit),
			unit: "day",
		});
	}
	if (record.effortUnit && record.effortUnit !== "hour" && record.effort != null) {
		record.set("fullEffort", {
			magnitude: fromDisplayWork(record.effort, record.effortUnit),
			unit: "hour",
		});
	}
};

export const _resetDisplayUnitSettingsForTests = () => {
	Object.assign(cache, DEFAULTS);
	cache.labels = null;
};
